/**
 * Khaleej Gamers - Collection Page JavaScript
 * Handles filtering, sorting, view switching, and interactions
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================
  const SELECTORS = {
    collection: '.kg-collection',
    filtersPanel: '[data-filters-panel]',
    filtersForm: '[data-filters-form]',
    filterToggle: '[data-filter-toggle]',
    filterGroup: '[data-filter-group]',
    filterGroupToggle: '[data-filter-toggle-group]',
    filterContent: '[data-filter-content]',
    filterCheckbox: '[data-filter-checkbox]',
    filterSearch: '[data-filter-search]',
    filterOption: '[data-filter-option]',
    filtersClose: '[data-filters-close]',
    filtersOverlay: '[data-filters-overlay]',
    mobileFilterToggle: '[data-mobile-filter-toggle]',
    
    // Price filter
    priceFilter: '[data-price-filter]',
    priceSliderMin: '[data-price-slider-min]',
    priceSliderMax: '[data-price-slider-max]',
    priceMinDisplay: '[data-price-min-display]',
    priceMaxDisplay: '[data-price-max-display]',
    priceMinInput: '[data-price-min-input]',
    priceMaxInput: '[data-price-max-input]',
    priceRange: '[data-price-range]',
    priceApply: '[data-price-apply]',
    
    // Layout
    layoutMenu: '[data-layout-menu]',
    layoutMenuToggle: '[data-layout-menu-toggle]',
    layoutDropdown: '[data-layout-dropdown]',
    layoutOption: '[data-filter-layout]',
    
    // Sorting
    sortSelect: '[data-sort-select]',
    
    // View switching
    viewSwitcher: '[data-view-switcher]',
    viewBtn: '[data-view]',
    productsGrid: '[data-products-grid]',
    productsContainer: '[data-products-container]',
    productsCount: '[data-products-count]',
    
    // Product card
    productCard: '[data-product-card]',
    cardActions: '[data-card-actions]',
    qtyDecrease: '[data-qty-decrease]',
    qtyIncrease: '[data-qty-increase]',
    qtyInput: '[data-qty-input]',
    addToCart: '[data-add-to-cart]',
    buyNow: '[data-buy-now]',
    wishlistToggle: '[data-wishlist-toggle]',
    notifyMe: '[data-notify-me]',
    
    // List filter
    listFilter: '[data-list-filter]',
    showMore: '[data-show-more]',
    showMoreText: '[data-show-more-text]'
  };

  const STORAGE_KEYS = {
    filterLayout: 'kg-filter-layout',
    gridView: 'kg-grid-view'
  };

  // ============================================
  // MAIN COLLECTION CLASS
  // ============================================
  class KGCollection {
    constructor(container) {
      this.container = container;
      this.filtersPanel = container.querySelector(SELECTORS.filtersPanel);
      this.filtersForm = container.querySelector(SELECTORS.filtersForm);
      this.productsGrid = container.querySelector(SELECTORS.productsGrid);
      this.productsContainer = container.querySelector(SELECTORS.productsContainer);
      
      this.isFiltersOpen = false;
      this.currentView = 'grid-4';
      this.filterLayout = 'vertical-left';
      
      this.init();
    }

    init() {
      this.loadSavedPreferences();
      this.bindEvents();
      this.initPriceFilters();
      this.initFilterGroups();
      this.initListFilters();
    }

    loadSavedPreferences() {
      // Load saved grid view
      const savedView = sessionStorage.getItem(STORAGE_KEYS.gridView);
      if (savedView && this.productsGrid) {
        this.currentView = savedView;
        this.productsGrid.setAttribute('data-view', savedView);
        this.updateViewButtons(savedView);
      }

      // Load saved filter layout
      const savedLayout = sessionStorage.getItem(STORAGE_KEYS.filterLayout);
      if (savedLayout) {
        this.filterLayout = savedLayout;
        this.applyFilterLayout(savedLayout);
      }
    }

    bindEvents() {
      // Filter toggle (works for both desktop and mobile)
      const filterToggle = this.container.querySelector(SELECTORS.filterToggle);
      if (filterToggle) {
        filterToggle.addEventListener('click', () => {
          // Check if mobile (screen width < 1024px)
          if (window.innerWidth < 1024) {
            this.openMobileFilters();
          } else {
            this.toggleFilters();
          }
        });
      }

      // Filters close button
      const filtersClose = this.container.querySelector(SELECTORS.filtersClose);
      if (filtersClose) {
        filtersClose.addEventListener('click', () => this.closeMobileFilters());
      }

      // Filters overlay
      const filtersOverlay = this.container.querySelector(SELECTORS.filtersOverlay);
      if (filtersOverlay) {
        filtersOverlay.addEventListener('click', () => this.closeMobileFilters());
      }

      // Layout menu
      this.initLayoutMenu();

      // Sort select
      const sortSelect = this.container.querySelector(SELECTORS.sortSelect);
      if (sortSelect) {
        sortSelect.addEventListener('change', (e) => this.handleSort(e.target.value));
      }

      // View switcher
      this.initViewSwitcher();

      // Filter checkboxes (instant filtering)
      this.container.querySelectorAll(SELECTORS.filterCheckbox).forEach(checkbox => {
        checkbox.addEventListener('change', () => this.applyFilters());
      });

      // Product card interactions
      this.initProductCards();
    }

    // ============================================
    // FILTER PANEL
    // ============================================
    toggleFilters() {
      const filterToggle = this.container.querySelector(SELECTORS.filterToggle);
      const isExpanded = filterToggle?.getAttribute('aria-expanded') === 'true';
      
      if (isExpanded) {
        filterToggle?.setAttribute('aria-expanded', 'false');
        this.filtersPanel?.classList.add('is-collapsed');
      } else {
        filterToggle?.setAttribute('aria-expanded', 'true');
        this.filtersPanel?.classList.remove('is-collapsed');
      }
    }

    openMobileFilters() {
      this.filtersPanel?.classList.add('is-open');
      this.container.querySelector(SELECTORS.filtersOverlay)?.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }

    closeMobileFilters() {
      this.filtersPanel?.classList.remove('is-open');
      this.container.querySelector(SELECTORS.filtersOverlay)?.classList.remove('is-visible');
      document.body.style.overflow = '';
    }

    // ============================================
    // FILTER GROUPS
    // ============================================
    initFilterGroups() {
      this.container.querySelectorAll(SELECTORS.filterGroupToggle).forEach(toggle => {
        toggle.addEventListener('click', () => {
          const isExpanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', !isExpanded);
        });
      });
    }

    // ============================================
    // LIST FILTERS (Search & Show More)
    // ============================================
    initListFilters() {
      // Filter search
      this.container.querySelectorAll(SELECTORS.filterSearch).forEach(search => {
        search.addEventListener('input', (e) => this.handleFilterSearch(e));
      });

      // Show more buttons
      this.container.querySelectorAll(SELECTORS.showMore).forEach(btn => {
        btn.addEventListener('click', (e) => this.handleShowMore(e));
      });
    }

    handleFilterSearch(e) {
      const searchValue = e.target.value.toLowerCase();
      const listFilter = e.target.closest(SELECTORS.listFilter);
      const options = listFilter?.querySelectorAll(SELECTORS.filterOption);

      options?.forEach(option => {
        const value = option.getAttribute('data-search-value') || '';
        if (value.includes(searchValue)) {
          option.classList.remove('kg-list-filter__option--hidden');
        } else {
          option.classList.add('kg-list-filter__option--hidden');
        }
      });
    }

    handleShowMore(e) {
      const btn = e.currentTarget;
      const listFilter = btn.closest(SELECTORS.listFilter);
      const isExpanded = btn.getAttribute('aria-expanded') === 'true';
      
      btn.setAttribute('aria-expanded', !isExpanded);
      listFilter?.classList.toggle('kg-list-filter--expanded');
      
      const textEl = btn.querySelector(SELECTORS.showMoreText);
      if (textEl) {
        const totalItems = listFilter?.querySelectorAll(SELECTORS.filterOption).length || 0;
        textEl.textContent = isExpanded ? `Show ${totalItems - 8} more` : 'Show less';
      }
    }

    // ============================================
    // PRICE FILTER
    // ============================================
    initPriceFilters() {
      this.container.querySelectorAll(SELECTORS.priceFilter).forEach(filter => {
        const sliderMin = filter.querySelector(SELECTORS.priceSliderMin);
        const sliderMax = filter.querySelector(SELECTORS.priceSliderMax);
        const displayMin = filter.querySelector(SELECTORS.priceMinDisplay);
        const displayMax = filter.querySelector(SELECTORS.priceMaxDisplay);
        const inputMin = filter.querySelector(SELECTORS.priceMinInput);
        const inputMax = filter.querySelector(SELECTORS.priceMaxInput);
        const range = filter.querySelector(SELECTORS.priceRange);
        const applyBtn = filter.querySelector(SELECTORS.priceApply);

        if (!sliderMin || !sliderMax) return;

        const rangeMin = parseInt(sliderMin.min);
        const rangeMax = parseInt(sliderMin.max);
        let initialMin = parseInt(sliderMin.value);
        let initialMax = parseInt(sliderMax.value);

        const updateRange = () => {
          const minVal = parseInt(sliderMin.value);
          const maxVal = parseInt(sliderMax.value);
          
          const minPercent = ((minVal - rangeMin) / (rangeMax - rangeMin)) * 100;
          const maxPercent = ((maxVal - rangeMin) / (rangeMax - rangeMin)) * 100;
          
          if (range) {
            range.style.left = minPercent + '%';
            range.style.right = (100 - maxPercent) + '%';
          }

          if (displayMin) displayMin.value = minVal;
          if (displayMax) displayMax.value = maxVal;

          // Check if values changed
          const hasChanges = minVal !== initialMin || maxVal !== initialMax;
          applyBtn?.classList.toggle('has-changes', hasChanges);
        };

        // Slider events
        sliderMin.addEventListener('input', () => {
          const minVal = parseInt(sliderMin.value);
          const maxVal = parseInt(sliderMax.value);
          if (minVal > maxVal) {
            sliderMin.value = maxVal;
          }
          updateRange();
        });

        sliderMax.addEventListener('input', () => {
          const minVal = parseInt(sliderMin.value);
          const maxVal = parseInt(sliderMax.value);
          if (maxVal < minVal) {
            sliderMax.value = minVal;
          }
          updateRange();
        });

        // Display input events
        displayMin?.addEventListener('change', () => {
          let val = parseInt(displayMin.value) || rangeMin;
          val = Math.max(rangeMin, Math.min(val, parseInt(sliderMax.value)));
          sliderMin.value = val;
          displayMin.value = val;
          updateRange();
        });

        displayMax?.addEventListener('change', () => {
          let val = parseInt(displayMax.value) || rangeMax;
          val = Math.min(rangeMax, Math.max(val, parseInt(sliderMin.value)));
          sliderMax.value = val;
          displayMax.value = val;
          updateRange();
        });

    // Apply button
    applyBtn?.addEventListener('click', () => {
      const minVal = parseInt(sliderMin.value);
      const maxVal = parseInt(sliderMax.value);
      
      // Shopify expects the value in the same format as the slider (already in correct units)
      if (inputMin) inputMin.value = minVal;
      if (inputMax) inputMax.value = maxVal;
      
      this.applyFilters();
    });

        // Initial update
        updateRange();
      });
    }

    // ============================================
    // LAYOUT MENU
    // ============================================
    initLayoutMenu() {
      const layoutMenu = this.container.querySelector(SELECTORS.layoutMenu);
      const layoutToggle = this.container.querySelector(SELECTORS.layoutMenuToggle);
      const layoutOptions = this.container.querySelectorAll(SELECTORS.layoutOption);

      if (layoutToggle) {
        layoutToggle.addEventListener('click', () => {
          layoutMenu?.classList.toggle('is-open');
        });

        // Close on outside click
        document.addEventListener('click', (e) => {
          if (!layoutMenu?.contains(e.target)) {
            layoutMenu?.classList.remove('is-open');
          }
        });
      }

      layoutOptions.forEach(option => {
        option.addEventListener('click', () => {
          const layout = option.getAttribute('data-filter-layout');
          this.applyFilterLayout(layout);
          sessionStorage.setItem(STORAGE_KEYS.filterLayout, layout);
          
          // Update active state
          layoutOptions.forEach(opt => opt.classList.remove('is-active'));
          option.classList.add('is-active');
          
          layoutMenu?.classList.remove('is-open');
        });
      });
    }

    applyFilterLayout(layout) {
      const main = this.container.querySelector('.kg-collection__main');
      if (!main) return;

      const [style, position] = layout.split('-');
      
      main.setAttribute('data-filter-style', style);
      if (position) {
        main.setAttribute('data-filter-position', position);
      }
    }

    // ============================================
    // VIEW SWITCHER
    // ============================================
    initViewSwitcher() {
      const viewBtns = this.container.querySelectorAll(SELECTORS.viewBtn);
      
      viewBtns.forEach(btn => {
        btn.addEventListener('click', () => {
          const view = btn.getAttribute('data-view');
          this.setView(view);
          this.updateViewButtons(view);
          sessionStorage.setItem(STORAGE_KEYS.gridView, view);
        });
      });
    }

    setView(view) {
      this.currentView = view;
      if (this.productsGrid) {
        this.productsGrid.setAttribute('data-view', view);
      }
    }

    updateViewButtons(activeView) {
      const viewBtns = this.container.querySelectorAll(SELECTORS.viewBtn);
      viewBtns.forEach(btn => {
        const view = btn.getAttribute('data-view');
        btn.classList.toggle('is-active', view === activeView);
      });
    }

    // ============================================
    // SORTING
    // ============================================
    handleSort(sortValue) {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sortValue);
      this.navigateTo(url.toString());
    }

    // ============================================
    // APPLY FILTERS
    // ============================================
    applyFilters() {
      if (!this.filtersForm) return;

      const formData = new FormData(this.filtersForm);
      const url = new URL(window.location.pathname, window.location.origin);
      
      // Add all form data to URL
      for (const [key, value] of formData.entries()) {
        if (value) {
          url.searchParams.append(key, value);
        }
      }

      // Preserve sort order
      const currentUrl = new URL(window.location.href);
      const sortBy = currentUrl.searchParams.get('sort_by');
      if (sortBy) {
        url.searchParams.set('sort_by', sortBy);
      }

      this.navigateTo(url.toString());
    }

    // ============================================
    // NAVIGATION
    // ============================================
    navigateTo(url) {
      // Show loading state
      this.productsContainer?.classList.add('kg-products--loading');
      
      // Navigate to new URL
      window.location.href = url;
    }

    // ============================================
    // PRODUCT CARDS
    // ============================================
    initProductCards() {
      // Quantity controls
      this.container.querySelectorAll(SELECTORS.productCard).forEach(card => {
        const qtyDecrease = card.querySelector(SELECTORS.qtyDecrease);
        const qtyIncrease = card.querySelector(SELECTORS.qtyIncrease);
        const qtyInput = card.querySelector(SELECTORS.qtyInput);

        qtyDecrease?.addEventListener('click', () => {
          const currentVal = parseInt(qtyInput?.value) || 1;
          if (currentVal > 1 && qtyInput) {
            qtyInput.value = currentVal - 1;
          }
        });

        qtyIncrease?.addEventListener('click', () => {
          const currentVal = parseInt(qtyInput?.value) || 1;
          const max = parseInt(qtyInput?.max) || 99;
          if (currentVal < max && qtyInput) {
            qtyInput.value = currentVal + 1;
          }
        });

        // Add to Cart
        const addToCartBtn = card.querySelector(SELECTORS.addToCart);
        addToCartBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleAddToCart(card);
        });

        // Buy Now
        const buyNowBtn = card.querySelector(SELECTORS.buyNow);
        buyNowBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleBuyNow(card);
        });

        // Wishlist
        const wishlistBtn = card.querySelector(SELECTORS.wishlistToggle);
        wishlistBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleWishlistToggle(wishlistBtn);
        });

        // Notify Me
        const notifyBtn = card.querySelector(SELECTORS.notifyMe);
        notifyBtn?.addEventListener('click', (e) => {
          e.preventDefault();
          this.handleNotifyMe(card);
        });
      });
    }

    async handleAddToCart(card) {
      const variantId = card.querySelector(SELECTORS.addToCart)?.getAttribute('data-variant-id');
      const quantity = parseInt(card.querySelector(SELECTORS.qtyInput)?.value) || 1;
      
      if (!variantId) return;

      const addBtn = card.querySelector(SELECTORS.addToCart);
      const originalText = addBtn?.innerHTML;
      
      try {
        addBtn.disabled = true;
        addBtn.innerHTML = '<span>Adding...</span>';

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity
          })
        });

        if (response.ok) {
          addBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg><span>Added!</span>';
          
          // Trigger cart update event
          document.dispatchEvent(new CustomEvent('cart:updated'));
          
          setTimeout(() => {
            addBtn.innerHTML = originalText;
            addBtn.disabled = false;
          }, 2000);
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (error) {
        console.error('Add to cart error:', error);
        addBtn.innerHTML = '<span>Error</span>';
        setTimeout(() => {
          addBtn.innerHTML = originalText;
          addBtn.disabled = false;
        }, 2000);
      }
    }

    async handleBuyNow(card) {
      const variantId = card.querySelector(SELECTORS.buyNow)?.getAttribute('data-variant-id');
      const quantity = parseInt(card.querySelector(SELECTORS.qtyInput)?.value) || 1;
      
      if (!variantId) return;

      const buyBtn = card.querySelector(SELECTORS.buyNow);
      buyBtn.disabled = true;
      buyBtn.innerHTML = '<span>Processing...</span>';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            id: variantId,
            quantity: quantity
          })
        });

        if (response.ok) {
          window.location.href = '/checkout';
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (error) {
        console.error('Buy now error:', error);
        buyBtn.innerHTML = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg><span>Buy Now</span>';
        buyBtn.disabled = false;
      }
    }

    handleWishlistToggle(btn) {
      btn.classList.toggle('is-active');
      
      const productId = btn.getAttribute('data-product-id');
      const isActive = btn.classList.contains('is-active');
      
      // Get existing wishlist from localStorage
      let wishlist = JSON.parse(localStorage.getItem('kg-wishlist') || '[]');
      
      if (isActive) {
        if (!wishlist.includes(productId)) {
          wishlist.push(productId);
        }
      } else {
        wishlist = wishlist.filter(id => id !== productId);
      }
      
      localStorage.setItem('kg-wishlist', JSON.stringify(wishlist));
      
      // Trigger wishlist update event
      document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { wishlist } }));
    }

    handleNotifyMe(card) {
      // This would typically open a modal for email capture
      const productTitle = card.querySelector('.kg-product-card__title a')?.textContent;
      alert(`We'll notify you when "${productTitle}" is back in stock!`);
    }

  }

  // ============================================
  // INITIALIZE
  // ============================================
  function init() {
    const collection = document.querySelector(SELECTORS.collection);
    if (collection) {
      new KGCollection(collection);
    }

    // Initialize wishlist state from localStorage
    const wishlist = JSON.parse(localStorage.getItem('kg-wishlist') || '[]');
    wishlist.forEach(productId => {
      const btn = document.querySelector(`[data-wishlist-toggle][data-product-id="${productId}"]`);
      btn?.classList.add('is-active');
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
